import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LatexText } from '@/components/ui/LatexText';
import { Loader2, ImagePlus, X, Plus, Save, Eye, EyeOff, ChevronDown, ChevronRight, Search, BookOpen } from 'lucide-react';
import { getFileUrl, uploadFile, fetchQuestionDetail, updateQuestion, QuestionDraft } from '@/api/qb';
import { useLookupStore } from '@/store/lookupStore';
import { vanillaClient } from '@/lib/trpc';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SingleQuestionEditorProps {
  questionId: string;
  onSave?: () => void;
  onCancel?: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  choice: '单选题',
  multi_choice: '多选题',
  fill_blank: '填空题',
  true_false: '判断题',
  essay: '解答题',
  proof: '证明题',
  puzzle: '数阵图',
};

const DIFFICULTY_OPTIONS = [
  { value: '1', label: '1 - 简单' },
  { value: '2', label: '2 - 较易' },
  { value: '3', label: '3 - 中等' },
  { value: '4', label: '4 - 较难' },
  { value: '5', label: '5 - 困难' },
];

const OPTION_TYPES = ['choice', 'multi_choice'];

interface KPItem {
  kpCode: string;
  name: string;
  parentCode?: string | null;
  level?: number;
  subject?: string | null;
  stage?: string | null;
  module?: string | null;
}

// ─── Knowledge Point Picker ─────────────────────────────────────────

function KnowledgePointPicker({
  selected,
  onChange,
  subject,
  stage,
}: {
  selected: string[];
  onChange: (codes: string[]) => void;
  subject?: string;
  stage?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [kpList, setKpList] = useState<KPItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!expanded || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    vanillaClient.knowledgePoints.tree
      .query({ subject: subject || undefined, stage: stage || undefined })
      .then((data) => setKpList(data as KPItem[]))
      .catch(() => toast.error('加载知识点失败'))
      .finally(() => setLoading(false));
  }, [expanded, subject, stage]);

  const filtered = useMemo(() => {
    if (!searchText.trim()) return kpList;
    const kw = searchText.toLowerCase();
    return kpList.filter(
      (kp) => kp.name.toLowerCase().includes(kw) || kp.kpCode.toLowerCase().includes(kw)
    );
  }, [kpList, searchText]);

  const toggle = (code: string) => {
    onChange(
      selected.includes(code)
        ? selected.filter((c) => c !== code)
        : [...selected, code]
    );
  };

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-muted-foreground" />
          知识点
          {selected.length > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
              {selected.length}
            </span>
          )}
        </span>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="border-t p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="搜索知识点名称或编码..."
              className="h-8 pl-8 text-sm"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-0.5">
              {filtered.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-3">
                  {kpList.length === 0 ? '暂无知识点数据' : '无匹配结果'}
                </div>
              )}
              {filtered.map((kp) => (
                <label
                  key={kp.kpCode}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm"
                  style={{ paddingLeft: `${8 + (kp.level || 0) * 16}px` }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(kp.kpCode)}
                    onChange={() => toggle(kp.kpCode)}
                    className="rounded border-gray-300"
                  />
                  <span className="truncate">{kp.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{kp.kpCode}</span>
                </label>
              ))}
            </div>
          )}

          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1 border-t">
              {selected.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 text-xs bg-muted px-1.5 py-0.5 rounded"
                >
                  {kpList.find((k) => k.kpCode === code)?.name || code}
                  <button
                    type="button"
                    onClick={() => toggle(code)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Question Preview ───────────────────────────────────────────────

function QuestionPreview({ question }: { question: QuestionDraft }) {
  const typeLabel = TYPE_LABELS[question.type] || question.type || '未知题型';

  return (
    <div className="space-y-4">
      {/* Meta badges */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded">
          {typeLabel}
        </span>
        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
          难度 {question.difficulty || 3}
        </span>
        {question.subject && (
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
            {question.subject}
          </span>
        )}
        {question.grade && (
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
            {question.grade}
          </span>
        )}
      </div>

      {/* Stem */}
      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">题干</div>
        <div className="text-sm leading-relaxed bg-muted/30 rounded-lg p-3 min-h-[60px]">
          {question.content.stem ? (
            <LatexText>{question.content.stem}</LatexText>
          ) : (
            <span className="text-muted-foreground italic">（暂无题干）</span>
          )}
        </div>
      </div>

      {/* Options */}
      {OPTION_TYPES.includes(question.type) && question.options && question.options.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">选项</div>
          <div className="space-y-1.5">
            {question.options.map((opt) => {
              const isAnswer = question.answer.value.includes(opt.label);
              return (
                <div
                  key={opt.label}
                  className={cn(
                    "flex items-start gap-2 text-sm rounded-lg px-3 py-2 border",
                    isAnswer ? "border-green-300 bg-green-50" : "border-transparent bg-muted/30"
                  )}
                >
                  <span className={cn(
                    "font-bold shrink-0 w-5",
                    isAnswer ? "text-green-600" : "text-muted-foreground"
                  )}>
                    {opt.label}.
                  </span>
                  <span className="flex-1 min-w-0">
                    {opt.content ? <LatexText>{opt.content}</LatexText> : <span className="text-muted-foreground italic">（空）</span>}
                  </span>
                  {isAnswer && <span className="text-green-600 text-xs shrink-0">✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Answer (non-choice types) */}
      {!OPTION_TYPES.includes(question.type) && question.answer.value && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">答案</div>
          <div className="text-sm bg-green-50 border border-green-200 rounded-lg p-3">
            <LatexText>{question.answer.value}</LatexText>
          </div>
        </div>
      )}

      {/* Analysis */}
      {question.analysis && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">解析</div>
          <div className="text-sm bg-blue-50/50 border border-blue-100 rounded-lg p-3">
            <LatexText>{question.analysis}</LatexText>
          </div>
        </div>
      )}

      {/* Knowledge points */}
      {question.knowledge_points && question.knowledge_points.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">知识点</div>
          <div className="flex flex-wrap gap-1">
            {question.knowledge_points.map((kp) => (
              <span key={kp} className="text-xs bg-muted px-2 py-0.5 rounded">{kp}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Editor ────────────────────────────────────────────────────

export const SingleQuestionEditor: React.FC<SingleQuestionEditorProps> = ({ questionId, onSave, onCancel }) => {
  const [question, setQuestion] = useState<QuestionDraft | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPreviewMobile, setShowPreviewMobile] = useState(false);

  // Lookup data
  const { subjects, grades, fetchAll, fetchGradesByStage, fetchSubjectsByStage } = useLookupStore();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Load grades/subjects based on question's stage
  const questionStage = question?.stage || '小学';
  useEffect(() => {
    if (questionStage) {
      fetchGradesByStage(questionStage);
      fetchSubjectsByStage(questionStage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionStage]);

  useEffect(() => {
    const loadQuestion = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await fetchQuestionDetail(questionId);
        // Ensure data structure integrity
        if (!data.content) data.content = { stem: '', images: [] };
        if (!data.content.images) data.content.images = [];
        if (!data.answer) data.answer = { value: '', images: [] };
        if (!data.answer.images) data.answer.images = [];
        if (!data.knowledge_points) data.knowledge_points = [];
        if (OPTION_TYPES.includes(data.type) && (!data.options || data.options.length === 0)) {
          data.options = [
            { label: 'A', content: '' },
            { label: 'B', content: '' },
            { label: 'C', content: '' },
            { label: 'D', content: '' },
          ];
        }
        setQuestion(data);
        setInitialSnapshot(JSON.stringify(data));
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : '加载题目失败';
        setLoadError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    if (questionId) {
      loadQuestion();
    }
  }, [questionId]);

  const isDirty = question ? JSON.stringify(question) !== initialSnapshot : false;

  const handleSave = async () => {
    if (!question) return;

    // Validation
    if (!question.content.stem.trim()) {
      toast.error('题干不能为空');
      return;
    }
    if (OPTION_TYPES.includes(question.type) && !question.answer.value.trim()) {
      toast.error('请选择正确答案');
      return;
    }

    setSaving(true);
    try {
      await updateQuestion(questionId, {
        ...question,
        knowledgePoints: question.knowledge_points || [],
      });
      toast.success('题目已更新');
      setInitialSnapshot(JSON.stringify(question));
      onSave?.();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '保存失败';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const normalizeImages = (images?: string[]) => {
    return (images || []).filter((img) => img && (img.startsWith('http') || img.startsWith('/')));
  };

  const handleImageUpload = async (
    file: File,
    target: 'content' | 'answer' | 'option' | 'analysis' = 'content',
    optionIndex?: number
  ) => {
    if (!question) return;
    try {
      const response = await uploadFile(file);
      const imageUrl = response.url;

      if (target === 'content') {
        const newStem = (question.content.stem || '') + `\n![image](${imageUrl})\n`;
        const newImages = [...normalizeImages(question.content.images), imageUrl];
        setQuestion({
          ...question,
          content: { ...question.content, stem: newStem, images: newImages },
        });
      } else if (target === 'answer') {
        const newValue = (question.answer.value || '') + `\n![image](${imageUrl})\n`;
        const newImages = [...normalizeImages(question.answer.images), imageUrl];
        setQuestion({
          ...question,
          answer: { ...question.answer, value: newValue, images: newImages },
        });
      } else if (target === 'analysis') {
        const newAnalysis = (question.analysis || '') + `\n![image](${imageUrl})\n`;
        setQuestion({ ...question, analysis: newAnalysis });
      } else if (target === 'option' && typeof optionIndex === 'number') {
        const options = [...(question.options || [])];
        const currentOption = options[optionIndex];
        const newContent = (currentOption.content || '') + ` ![image](${imageUrl})`;
        const newImages = [...normalizeImages(currentOption.images), imageUrl];
        options[optionIndex] = { ...currentOption, content: newContent, images: newImages };
        setQuestion({ ...question, options });
      }
    } catch {
      toast.error('上传图片失败');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!question) {
    return <div className="text-center text-red-500 p-4">题目加载失败{loadError ? `: ${loadError}` : ''}</div>;
  }

  const isOptionType = OPTION_TYPES.includes(question.type);
  const currentSubjects = subjects.filter((s) => s.type === 'regular' || !s.type);

  return (
    <div className="space-y-4 pb-20">
      {/* Sticky action bar */}
      <div className="flex items-center justify-between sticky top-0 bg-background z-10 py-2 border-b gap-2">
        <h3 className="font-medium text-lg truncate">
          编辑题目
          {isDirty && <span className="ml-2 text-xs text-orange-500 font-normal">未保存</span>}
        </h3>
        <div className="flex gap-2 shrink-0">
          {/* Mobile preview toggle */}
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden"
            onClick={() => setShowPreviewMobile(!showPreviewMobile)}
          >
            {showPreviewMobile ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            预览
          </Button>
          {onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>取消</Button>}
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            保存修改
          </Button>
        </div>
      </div>

      {/* Main layout: form + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ─── Left: Edit Form ─── */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">题型</label>
              <Select
                value={question.type}
                onValueChange={(value) => {
                  const updates: Partial<QuestionDraft> = { type: value };
                  if (OPTION_TYPES.includes(value) && (!question.options || question.options.length === 0)) {
                    updates.options = [
                      { label: 'A', content: '' },
                      { label: 'B', content: '' },
                      { label: 'C', content: '' },
                      { label: 'D', content: '' },
                    ];
                  }
                  // Reset answer if switching away from compatible type
                  if (OPTION_TYPES.includes(value) && !OPTION_TYPES.includes(question.type)) {
                    updates.answer = { ...question.answer, value: '' };
                  }
                  setQuestion({ ...question, ...updates });
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="题型" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">难度</label>
              <Select
                value={String(question.difficulty || 3)}
                onValueChange={(value) => setQuestion({ ...question, difficulty: parseInt(value) || 3 })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="难度" />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">学科</label>
              <Select
                value={question.subject || ''}
                onValueChange={(value) => setQuestion({ ...question, subject: value })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="学科" />
                </SelectTrigger>
                <SelectContent>
                  {currentSubjects.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">年级</label>
              <Select
                value={question.grade || ''}
                onValueChange={(value) => setQuestion({ ...question, grade: value })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="年级" />
                </SelectTrigger>
                <SelectContent>
                  {grades.map((g) => (
                    <SelectItem key={g.code} value={g.code}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stem */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">题干</label>
            <Textarea
              value={question.content.stem}
              onChange={(e) => setQuestion({
                ...question,
                content: { ...question.content, stem: e.target.value },
              })}
              className="min-h-[120px]"
              placeholder="输入题干内容，支持 LaTeX 公式（$...$）和 Markdown 图片语法..."
            />
            {/* Stem images */}
            <div className="flex flex-wrap gap-2 mt-2">
              {question.content.images?.map((img, imgIdx) => (
                <div key={imgIdx} className="relative group w-20 h-20 border rounded overflow-hidden">
                  <img src={getFileUrl(img)} className="w-full h-full object-cover" alt="Question Image" />
                  <button
                    onClick={() => {
                      const newImages = question.content.images.filter((_, i) => i !== imgIdx);
                      setQuestion({ ...question, content: { ...question.content, images: newImages } });
                    }}
                    className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors">
                <ImagePlus className="w-6 h-6" />
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleImageUpload(e.target.files[0], 'content');
                    }
                  }}
                />
              </label>
            </div>
          </div>

          {/* Options (choice / multi_choice) */}
          {isOptionType && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">选项</label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    const currentOptions = question.options || [];
                    const usedLabels = new Set(currentOptions.map((o) => o.label));
                    let nextLabelCode = 65;
                    while (usedLabels.has(String.fromCharCode(nextLabelCode))) {
                      nextLabelCode++;
                    }
                    const nextLabel = String.fromCharCode(nextLabelCode);
                    setQuestion({
                      ...question,
                      options: [...currentOptions, { label: nextLabel, content: '' }],
                    });
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" /> 添加选项
                </Button>
              </div>
              {question.options?.map((opt, optIdx) => (
                <div key={optIdx} className="relative group/opt p-2 border rounded-md border-transparent hover:border-gray-200 transition-colors">
                  <div className="flex gap-2 items-center">
                    <div className="w-8 flex items-center justify-center bg-gray-100 rounded text-sm font-bold shrink-0 h-9">
                      {opt.label}
                    </div>
                    <Input
                      value={opt.content}
                      onChange={(e) => {
                        const newOptions = [...(question.options || [])];
                        newOptions[optIdx] = { ...opt, content: e.target.value };
                        setQuestion({ ...question, options: newOptions });
                      }}
                      className="flex-1"
                      placeholder={`选项 ${opt.label} 内容`}
                    />
                    <label className="cursor-pointer text-gray-400 hover:text-blue-500 p-2 transition-colors" title="添加图片">
                      <ImagePlus className="w-4 h-4" />
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleImageUpload(e.target.files[0], 'option', optIdx);
                          }
                        }}
                      />
                    </label>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-gray-400 hover:text-red-500 opacity-0 group-hover/opt:opacity-100 transition-opacity"
                      onClick={() => {
                        const newOptions = [...(question.options || [])];
                        newOptions.splice(optIdx, 1);
                        for (let i = optIdx; i < newOptions.length; i++) {
                          newOptions[i].label = String.fromCharCode(65 + i);
                        }
                        setQuestion({ ...question, options: newOptions });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {opt.images && opt.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 ml-10">
                      {opt.images.map((img, imgIdx) => (
                        <div key={imgIdx} className="relative group/img w-16 h-16 border rounded overflow-hidden">
                          <img src={getFileUrl(img)} className="w-full h-full object-cover" alt={`Option ${opt.label} Image`} />
                          <button
                            onClick={() => {
                              const newOptions = [...(question.options || [])];
                              const currentImages = newOptions[optIdx].images || [];
                              newOptions[optIdx] = {
                                ...newOptions[optIdx],
                                images: currentImages.filter((_, i) => i !== imgIdx),
                              };
                              setQuestion({ ...question, options: newOptions });
                            }}
                            className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover/img:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Answer */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">答案</label>

            {question.type === 'choice' && (
              <div className="flex flex-wrap gap-2">
                {(question.options || []).map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setQuestion({ ...question, answer: { ...question.answer, value: opt.label } })}
                    className={cn(
                      "w-10 h-10 rounded-lg border-2 font-bold text-sm transition-colors",
                      question.answer.value === opt.label
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-green-300 hover:bg-green-50/50"
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
                <span className="text-xs text-muted-foreground self-center ml-1">点击选择正确答案</span>
              </div>
            )}

            {question.type === 'multi_choice' && (
              <div className="flex flex-wrap gap-2">
                {(question.options || []).map((opt) => {
                  const isSelected = question.answer.value.includes(opt.label);
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        const current = question.answer.value.split('').filter(Boolean);
                        const next = isSelected
                          ? current.filter((l) => l !== opt.label)
                          : [...current, opt.label];
                        next.sort();
                        setQuestion({ ...question, answer: { ...question.answer, value: next.join('') } });
                      }}
                      className={cn(
                        "w-10 h-10 rounded-lg border-2 font-bold text-sm transition-colors",
                        isSelected
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-gray-200 text-gray-500 hover:border-green-300 hover:bg-green-50/50"
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                <span className="text-xs text-muted-foreground self-center ml-1">可选多个</span>
              </div>
            )}

            {question.type === 'true_false' && (
              <div className="flex gap-3">
                {['对', '错'].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setQuestion({ ...question, answer: { ...question.answer, value: val } })}
                    className={cn(
                      "px-6 py-2 rounded-lg border-2 font-medium text-sm transition-colors",
                      question.answer.value === val
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-green-300"
                    )}
                  >
                    {val === '对' ? '✓ 对' : '✗ 错'}
                  </button>
                ))}
              </div>
            )}

            {!['choice', 'multi_choice', 'true_false'].includes(question.type) && (
              <>
                <Textarea
                  value={question.answer.value}
                  onChange={(e) => setQuestion({
                    ...question,
                    answer: { ...question.answer, value: e.target.value },
                  })}
                  rows={4}
                  placeholder="输入答案，支持 LaTeX..."
                />
                {/* Answer images */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {question.answer.images?.map((img, imgIdx) => (
                    <div key={imgIdx} className="relative group w-12 h-12 border rounded overflow-hidden">
                      <img src={getFileUrl(img)} className="w-full h-full object-cover" alt="Ans Img" />
                      <button
                        onClick={() => {
                          const newImages = question.answer.images.filter((_, i) => i !== imgIdx);
                          setQuestion({ ...question, answer: { ...question.answer, images: newImages } });
                        }}
                        className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-12 h-12 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors">
                    <ImagePlus className="w-4 h-4" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleImageUpload(e.target.files[0], 'answer');
                        }
                      }}
                    />
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Analysis */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">解析</label>
            <Textarea
              value={question.analysis || ''}
              onChange={(e) => setQuestion({ ...question, analysis: e.target.value })}
              rows={6}
              placeholder="输入解题过程和解析，支持 LaTeX..."
            />
            {/* Analysis images */}
            <div className="flex flex-wrap gap-2 mt-2">
              <label className="w-12 h-12 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors" title="上传解析图片">
                <ImagePlus className="w-4 h-4" />
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleImageUpload(e.target.files[0], 'analysis');
                    }
                  }}
                />
              </label>
            </div>
          </div>

          {/* Knowledge Points */}
          <KnowledgePointPicker
            selected={question.knowledge_points || []}
            onChange={(codes) => setQuestion({ ...question, knowledge_points: codes })}
            subject={question.subject}
            stage={question.stage}
          />
        </div>

        {/* ─── Right: Preview (desktop always visible, mobile toggle) ─── */}
        <div className={cn("lg:block", showPreviewMobile ? "block" : "hidden")}>
          <div className="lg:sticky lg:top-16 border rounded-xl p-4 bg-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b">
              <Eye className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">实时预览</span>
            </div>
            <QuestionPreview question={question} />
          </div>
        </div>
      </div>
    </div>
  );
};
